import { useState, useEffect } from 'react'
import { Caption, COLORS, Panel } from './ui'

// CNN pipeline as shrinking feature-map VOLUMES (H×W down, channels up) — the
// accurate mental model. The old 3D scene drew a fully-connected MLP, which is
// the opposite of a CNN (conv layers are local + weight-shared, not dense).
const STAGES = [
  { label: 'Input',      dims: '224² × 3',   wh: 92, depth: 3,   color: '#6366f1', learns: 'raw pixels' },
  { label: 'Conv + ReLU', dims: '224² × 64',  wh: 80, depth: 7,   color: '#8b5cf6', learns: 'edges' },
  { label: 'Pool',       dims: '112² × 64',  wh: 60, depth: 7,   color: '#a855f7', learns: '↓ resolution' },
  { label: 'Conv + ReLU', dims: '112² × 128', wh: 50, depth: 9,   color: '#c026d3', learns: 'textures' },
  { label: 'Pool',       dims: '56² × 128',  wh: 36, depth: 9,   color: '#ec4899', learns: '↓ resolution' },
  { label: 'Conv + ReLU', dims: '28² × 256',  wh: 26, depth: 11,  color: '#f43f5e', learns: 'object parts' },
  { label: 'Pool → GAP',  dims: '1 × 256',    wh: 16, depth: 11,  color: '#f97316', learns: 'whole objects' },
  { label: 'Dense',      dims: '1000',       wh: 14, depth: 2,   color: '#eab308', learns: 'class scores' },
]

const PREDS = [
  { label: 'Tabby cat', p: 0.91, color: '#eab308' },
  { label: 'Egyptian cat', p: 0.05, color: '#a855f7' },
  { label: 'Lynx', p: 0.02, color: '#6366f1' },
]

export function CNN2D() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const scale = mobile ? 0.7 : 1
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center',
      gap: mobile ? 2 : 4,
      padding: mobile ? '80px 12px 90px' : '90px 24px 110px',
      color: COLORS.text, fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center',
    }}>
      {/* Pipeline */}
      {mobile ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, width: '100%', maxWidth: 340 }}>
          {STAGES.map((s, i) => (
            <Volume key={i} {...s} scale={scale} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap', maxWidth: 1100 }}>
          {STAGES.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Volume {...s} scale={scale} />
              {i < STAGES.length - 1 && <span style={{ color: COLORS.indigo, opacity: 0.5, fontSize: 18 }}>→</span>}
            </div>
          ))}
        </div>
      )}

      {/* Prediction */}
      <Panel glow="#eab308" style={{ marginTop: mobile ? 10 : 30, padding: mobile ? '10px 14px' : '14px 20px', minWidth: mobile ? 240 : 300, width: mobile ? '90%' : undefined }}>
        <div style={{ fontSize: mobile ? 9 : 11, letterSpacing: '0.14em', color: '#eab308', fontFamily: "'JetBrains Mono', ui-monospace, monospace", marginBottom: mobile ? 6 : 10 }}>
          SOFTMAX OUTPUT · TOP-3
        </div>
        {PREDS.map(p => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: mobile ? 6 : 10, marginBottom: mobile ? 4 : 6 }}>
            <div style={{ width: mobile ? 80 : 110, textAlign: 'right', fontSize: mobile ? 10 : 12, color: COLORS.textDim }}>{p.label}</div>
            <div style={{ flex: 1, height: mobile ? 6 : 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${p.p * 100}%`, height: '100%', background: p.color, borderRadius: 4 }} />
            </div>
            <div style={{ width: 36, textAlign: 'left', fontSize: mobile ? 10 : 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: '#fff' }}>{(p.p * 100).toFixed(0)}%</div>
          </div>
        ))}
      </Panel>

      <Caption>
        Each block shrinks the image in space (H×W) while growing in <strong>channels</strong> — stacking learned
        feature maps. Early layers fire on edges, deeper layers on textures, parts, then whole objects.
        Crucially, conv layers are <em>local and weight-shared</em>, not fully connected — that's what makes them efficient.
      </Caption>
    </div>
  )
}

// Faux-3D volume: a stack of offset rounded rectangles, sized by spatial dims.
function Volume({ wh, depth, color, label, dims, learns, scale = 1 }) {
  const layers = Math.min(depth, 6)
  const swh = Math.round(wh * scale)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: scale < 1 ? 3 : 6, width: Math.max(swh, 36) + 26 }}>
      <div style={{ position: 'relative', width: swh + layers * 3, height: swh + layers * 3 }}>
        {Array.from({ length: layers }).map((_, i) => (
          <div key={i} style={{
            position: 'absolute', left: i * 3, top: (layers - 1 - i) * 3, width: swh, height: swh,
            borderRadius: 4, background: `${color}22`, border: `1px solid ${color}`,
            boxShadow: i === layers - 1 ? `0 0 12px ${color}55` : 'none',
          }} />
        ))}
      </div>
      <div style={{ fontSize: scale < 1 ? 9 : 11, fontWeight: 600, color, lineHeight: 1.2 }}>{label}</div>
      <div style={{ fontSize: scale < 1 ? 8 : 9.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textFaint }}>{dims}</div>
      <div style={{ fontSize: scale < 1 ? 8 : 9.5, color: COLORS.textDim, fontStyle: 'italic' }}>{learns}</div>
    </div>
  )
}
